import { useCallback, useState } from "react";
import { AxiosInstance } from "../services/Axios";

const SCRAPING_PROXY_BASE =
  process.env.REACT_APP_SCRAPING_PROXY_URL ??
  process.env.REACT_APP_BACKEND_URL;

const scrapingProxyConfig = { baseURL: SCRAPING_PROXY_BASE };

const isLocalScrapingHost = Boolean(process.env.REACT_APP_SCRAPING_PROXY_URL);

const scrapingPath = (relativePath) =>
  isLocalScrapingHost ? relativePath : `scraping-proxy/${relativePath}`;

export function useCrossScraperKeywords() {
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchScrapers = useCallback(async (keyword, partialMatch = false) => {
    try {
      setSearchLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("keywords/search-scrapers"),
        {
          ...scrapingProxyConfig,
          params: {
            keyword,
            ...(partialMatch && { partial_match: "true" }),
          },
        },
      );
      setSearchResults(res.data);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      throw new Error(msg);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const massAdd = useCallback(async (keywords, projectNames) => {
    try {
      setActionLoading(true);
      setError(null);
      const list = Array.isArray(keywords) ? keywords : [keywords];
      const res = await AxiosInstance.post(
        scrapingPath("keywords/mass-add"),
        { keywords: list, project_names: projectNames },
        scrapingProxyConfig,
      );
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      throw new Error(msg);
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Delete keywords across scrapers. Pass `keywordIds` to delete a specific
  // selection of rows (from the search results), or `keyword` + `partialMatch`
  // to delete every row that matches the search.
  const massDelete = useCallback(
    async ({ keyword, keywordIds, partialMatch = false } = {}) => {
      try {
        setActionLoading(true);
        setError(null);
        const payload = keywordIds?.length
          ? { keyword_ids: keywordIds }
          : { keyword, ...(partialMatch && { partial_match: true }) };
        const res = await AxiosInstance.post(
          scrapingPath("keywords/mass-delete"),
          payload,
          scrapingProxyConfig,
        );
        return res.data;
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        setError(msg);
        throw new Error(msg);
      } finally {
        setActionLoading(false);
      }
    },
    [],
  );

  // Rename keywords across scrapers. Pass `keywordIds` to rename the exact rows
  // the search returned, or `keyword` + `partialMatch` to rename every row that
  // matches the search.
  const massEdit = useCallback(async ({ keyword, keywordIds, newKeyword, partialMatch = false }) => {
    try {
      setActionLoading(true);
      setError(null);
      const payload = keywordIds?.length
        ? { keyword_ids: keywordIds, new_keyword: newKeyword }
        : { old_keyword: keyword, new_keyword: newKeyword, ...(partialMatch && { partial_match: true }) };
      const res = await AxiosInstance.post(
        scrapingPath("keywords/mass-edit"),
        payload,
        scrapingProxyConfig,
      );
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      throw new Error(msg);
    } finally {
      setActionLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults(null);
    setError(null);
  }, []);

  return {
    searchResults,
    searchLoading,
    actionLoading,
    error,
    setError,
    searchScrapers,
    massAdd,
    massDelete,
    massEdit,
    clearSearch,
  };
}
